
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './StoryViewer.css';
import { Post, useApp } from '../../context/AppContext';
import { postsAPI } from '../../services/api';

interface StoryViewerProps {
  stories: Post[];
  initialIndex: number;
  onClose: () => void;
}

const IMAGE_DURATION = 5000;
const HOLD_THRESHOLD = 200;

const StoryViewer: React.FC<StoryViewerProps> = ({ stories, initialIndex, onClose }) => {
  const { adminUser } = useApp();
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [progress, setProgress] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [slideDir, setSlideDir] = useState<'next' | 'prev' | null>(null);
  const [animating, setAnimating] = useState(false);
  const [videoDuration, setVideoDuration] = useState<number | null>(null);

  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const progressRef = useRef(0);
  const holdTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isHoldingRef = useRef(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const isVideo = stories[currentIndex]?.mediaType === 'video';

  const currentStory = stories[currentIndex];

  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(onClose, 280);
  }, [onClose]);

  const navigate = useCallback((dir: 'next' | 'prev') => {
    if (animating) return;

    if (dir === 'next') {
      if (currentIndex >= stories.length - 1) {
        handleClose();
        return;
      }
    } else {
      if (currentIndex === 0) {
        // Restart current
        progressRef.current = 0;
        setProgress(0);
        return;
      }
    }

    setSlideDir(dir);
    setAnimating(true);
    progressRef.current = 0;
    setProgress(0);

    setTimeout(() => {
      setCurrentIndex(prev => dir === 'next' ? prev + 1 : prev - 1);
      setSlideDir(null);
      setTimeout(() => setAnimating(false), 50);
    }, 220);
  }, [animating, currentIndex, stories.length, handleClose]);

  // Reset video duration when story changes
  useEffect(() => {
    setVideoDuration(null);
    progressRef.current = 0;
    setProgress(0);
  }, [currentIndex]);

  // Record story view — fire & forget; backend dedupes per (story, viewer)
  // so repeat opens just bump the per-viewer counter, not the distinct
  // total. Server ignores admins so previewing your own story doesn't
  // pad the count.
  useEffect(() => {
    const story = stories[currentIndex];
    if (!story?.id) return;
    postsAPI.recordView(story.id).catch(err => {
      // Silent: view-tracking shouldn't break the story experience.
      console.debug('[StoryViewer] view record failed', err);
    });
  }, [currentIndex, stories]);

  // Progress timer — skipped for video (video drives its own progress via timeupdate)
  useEffect(() => {
    if (isPaused || animating || isVideo) return;

    const duration = IMAGE_DURATION;
    const interval = 50;
    const increment = (interval / duration) * 100;

    timerRef.current = setInterval(() => {
      progressRef.current += increment;
      setProgress(progressRef.current);
      if (progressRef.current >= 100) {
        if (timerRef.current) clearInterval(timerRef.current);
        navigate('next');
      }
    }, interval);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [currentIndex, isPaused, animating, isVideo, navigate]);

  // Keyboard
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') navigate('next');
      else if (e.key === 'ArrowLeft') navigate('prev');
      else if (e.key === 'Escape') handleClose();
      else if (e.key === ' ') { setIsPaused(p => !p); e.preventDefault(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [navigate, handleClose]);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  // Hold to pause — center area only
  const startHold = () => {
    isHoldingRef.current = false;
    holdTimerRef.current = setTimeout(() => {
      isHoldingRef.current = true;
      setIsPaused(true);
      if (videoRef.current) videoRef.current.pause();
    }, HOLD_THRESHOLD);
  };

  const endHold = () => {
    if (holdTimerRef.current) clearTimeout(holdTimerRef.current);
    if (isHoldingRef.current) {
      isHoldingRef.current = false;
      setIsPaused(false);
      if (videoRef.current) videoRef.current.play();
    }
  };

  // Swipe support
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    startHold();
  };

  const onTouchEnd = (e: React.TouchEvent) => {
    endHold();
    if (!isHoldingRef.current) {
      const dx = e.changedTouches[0].clientX - touchStartX.current;
      const dy = Math.abs(e.changedTouches[0].clientY - touchStartY.current);
      // Horizontal swipe
      if (Math.abs(dx) > 50 && dy < 60) {
        navigate(dx < 0 ? 'next' : 'prev');
      }
    }
  };

  const formatTime = (date: Date) => {
    const diff = Date.now() - date.getTime();
    const h = Math.floor(diff / 3600000);
    if (h < 1) return 'Just now';
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  };

  // Portal to document.body so the overlay can never be trapped inside a
  // transformed/filtered ancestor (e.g. `.feed { transform: translateZ(0) }`
  // creates a containing block that breaks `position: fixed`). Without the
  // portal the viewer renders below the page fold instead of overlaying it.
  const overlay = (
    <div className={`story-viewer-backdrop ${isClosing ? 'closing' : ''}`} onClick={handleClose}>
      <div
        className={`story-viewer ${isClosing ? 'closing' : ''}`}
        onClick={e => e.stopPropagation()}
      >
        {/* Progress bars */}
        <div className="story-progress-container">
          {stories.map((_, i) => (
            <div key={i} className="story-progress-bar" onClick={() => { progressRef.current = 0; setProgress(0); setCurrentIndex(i); }}>
              <div
                className="story-progress-fill"
                style={{
                  width: i < currentIndex ? '100%' : i === currentIndex ? `${progress}%` : '0%',
                  transition: i === currentIndex ? 'width 0.05s linear' : 'none'
                }}
              />
            </div>
          ))}
        </div>

        {/* Header */}
        <div className="story-viewer-header">
          <div className="story-viewer-user">
            <img src={adminUser.avatar} alt={adminUser.displayName} className="story-viewer-avatar" />
            <div className="story-viewer-info">
              <span className="story-viewer-name">{adminUser.displayName}</span>
              <span className="story-viewer-time">{formatTime(currentStory.timestamp)}</span>
            </div>
          </div>
          <div className="story-viewer-actions">
            <button className="story-action-btn" onClick={() => {
              setIsPaused(p => {
                const next = !p;
                if (videoRef.current) next ? videoRef.current.pause() : videoRef.current.play();
                return next;
              });
            }}>
              {isPaused
                ? <svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
                : <svg viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
              }
            </button>
            <button className="story-action-btn close-btn" onClick={handleClose}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M18 6L6 18M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Sliding story content — `--story-bg` powers the blurred matte
            so contained portrait/landscape media looks polished, not
            letterboxed. */}
        <div
          className={`story-slide-wrapper ${slideDir ? `slide-out-${slideDir}` : 'slide-in'}`}
          style={currentStory.mediaUrl && currentStory.mediaType !== 'video'
            ? ({ ['--story-bg' as any]: `url(${currentStory.mediaUrl})` })
            : undefined}
          onMouseDown={startHold}
          onMouseUp={endHold}
          onMouseLeave={endHold}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          {currentStory.mediaUrl && currentStory.mediaType === 'video' ? (
            <video
              key={currentStory.id}
              ref={videoRef}
              src={currentStory.mediaUrl}
              autoPlay
              playsInline
              // Must start muted for iOS Safari autoplay policy. Without
              // this, the video pauses on the first frame, the timer
              // never advances (onTimeUpdate's duration check never
              // becomes truthy), and the user is stuck. Users can tap
              // the unmute control overlay to enable audio.
              muted
              controls={false}
              className={`story-image ${isPaused ? 'paused' : ''}`}
              style={{ background: '#000' }}
              onLoadedMetadata={e => setVideoDuration((e.target as HTMLVideoElement).duration)}
              onTimeUpdate={e => {
                const vid = e.target as HTMLVideoElement;
                if (vid.duration > 0) {
                  progressRef.current = (vid.currentTime / vid.duration) * 100;
                  setProgress(progressRef.current);
                }
              }}
              onEnded={() => navigate('next')}
              onClick={e => {
                // Tap-to-unmute. Stops bubbling so the parent's
                // pause/swipe handlers don't fire on this exact touch.
                const v = e.currentTarget as HTMLVideoElement;
                if (v.muted) {
                  e.stopPropagation();
                  v.muted = false;
                }
              }}
            />
          ) : currentStory.mediaUrl ? (
            <img
              key={currentStory.id}
              src={currentStory.mediaUrl}
              alt="Story"
              className={`story-image ${isPaused ? 'paused' : ''}`}
            />
          ) : null}

          {currentStory.content && (
            <div className="story-text-overlay">
              <p>{currentStory.content}</p>
            </div>
          )}
        </div>

        {/* Tap zones — left and right */}
        <button
          className="story-tap-zone left"
          onClick={() => navigate('prev')}
          aria-label="Previous story"
        >
          <div className="tap-zone-arrow">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M15 18l-6-6 6-6"/>
            </svg>
          </div>
        </button>
        <button
          className="story-tap-zone right"
          onClick={() => navigate('next')}
          aria-label="Next story"
        >
          <div className="tap-zone-arrow">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M9 18l6-6-6-6"/>
            </svg>
          </div>
        </button>

        {/* Pause overlay */}
        {isPaused && (
          <div className="paused-indicator">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <rect x="6" y="4" width="4" height="16"/>
              <rect x="14" y="4" width="4" height="16"/>
            </svg>
          </div>
        )}

        {/* W7.4: emoji reactions row — taps emit a temporary floating
            burst and POST to the story-reactions endpoint. Hidden in
            admin view (admins shouldn't react to their own stories). */}
        {!adminUser?.isAdmin && (
          <StoryReactionsBar storyId={currentStory?.id} />
        )}
      </div>
    </div>
  );

  return createPortal(overlay, document.body);
};

const REACTION_EMOJIS = ['❤️', '🔥', '😍', '😂', '👀', '🙌'] as const;
const REACT_STORAGE_PREFIX = 'oj_story_react_';

interface StoryReactionsBarProps {
  storyId?: string;
}

const StoryReactionsBar: React.FC<StoryReactionsBarProps> = ({ storyId }) => {
  const [bursts, setBursts] = useState<Array<{ id: number; emoji: string }>>([]);
  const burstIdRef = useRef(0);

  const handleReact = (emoji: string) => {
    if (!storyId) return;
    // Optimistic burst animation.
    const id = ++burstIdRef.current;
    setBursts(b => [...b, { id, emoji }]);
    setTimeout(() => setBursts(b => b.filter(x => x.id !== id)), 1200);
    // Persist client-side intent (dedupe per user+story+emoji).
    try {
      const key = `${REACT_STORAGE_PREFIX}${storyId}`;
      const seen: string[] = JSON.parse(localStorage.getItem(key) || '[]');
      if (!seen.includes(emoji)) {
        localStorage.setItem(key, JSON.stringify([...seen, emoji].slice(-12)));
      }
    } catch {}
    // Best-effort POST. Endpoint may 404 until backend ships; we don't
    // surface that — the animation already gave the user feedback.
    try {
      const token = localStorage.getItem('onlyjalen_access_token');
      fetch(`https://onlyjalen-api.jalen1wa.workers.dev/api/posts/${storyId}/reactions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ emoji }),
      }).catch(() => {});
    } catch {}
  };

  return (
    <div className="story-reactions-bar" onClick={e => e.stopPropagation()}>
      {bursts.map(b => (
        <span key={b.id} className="story-react-burst" aria-hidden="true">{b.emoji}</span>
      ))}
      {REACTION_EMOJIS.map(emoji => (
        <button
          key={emoji}
          type="button"
          className="story-react-btn"
          onClick={() => handleReact(emoji)}
          aria-label={`React with ${emoji}`}
        >
          {emoji}
        </button>
      ))}
    </div>
  );
};

export default StoryViewer;
